import { InputNumber, Tooltip } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import { DadosComparativoDireto } from '@/features/laudo/types/laudo-wizard.types';
import type { DadosCalculados } from '../types';
import { formatarMoeda } from '../../shared/formatters';
import * as styles from '../styles';

type ColumnType = any;

// Estilos para células com valores zerados
const zeroCellStyle = {
  backgroundColor: '#fff7e6',
  border: '1px solid #ffc53d',
  borderRadius: 4,
  padding: '4px 8px',
};

const warningIconStyle = {
  color: '#fa8c16',
  marginLeft: 4,
  fontSize: 12,
};

export function buildColunasBase(displayIdMap?: Record<string, string>): ColumnType[] {
  return [
    {
      title: 'Amostra',
      dataIndex: 'amostra',
      key: 'amostra',
      width: 120,
      fixed: 'left' as const,
      render: (text: string) => (
        <strong style={styles.amostraDefaultStyle}>
          {displayIdMap?.[text] || text}
        </strong>
      ),
    },
    {
      title: 'Área (m²)',
      dataIndex: 'area',
      key: 'area',
      width: 120,
      render: (value: number) => {
        const isZerado = !value || value <= 0;

        if (isZerado) {
          return (
            <Tooltip title="Área não informada - preencha a área do imóvel">
              <div style={{ ...styles.areaCellStyle, ...zeroCellStyle }}>
                0.00 m²
                <WarningOutlined style={warningIconStyle} />
              </div>
            </Tooltip>
          );
        }

        return (
          <div style={styles.areaCellStyle}>
            {Number(value).toFixed(2)} m²
          </div>
        );
      },
    },
    {
      title: 'Valor (R$)',
      dataIndex: 'valorAnuncio',
      key: 'valorAnuncio',
      width: 150,
      render: (value: number) => {
        const isZerado = !value || value <= 0;

        if (isZerado) {
          return (
            <Tooltip title="Valor não informado - preencha o valor do anúncio">
              <div style={{ ...styles.valorCellStyle, ...zeroCellStyle }}>
                {formatarMoeda(0)}
                <WarningOutlined style={warningIconStyle} />
              </div>
            </Tooltip>
          );
        }

        return (
          <div style={styles.valorCellStyle}>
            {formatarMoeda(value)}
          </div>
        );
      },
    },
    {
      title: 'Valor/m²',
      dataIndex: 'valorM2',
      key: 'valorM2',
      width: 140,
      render: (value: number, record: DadosCalculados) => {
        const isZerado = value <= 0;
        const hasNoArea = !record.area || record.area <= 0;
        const hasNoValor = !record.valorAnuncio || record.valorAnuncio <= 0;

        if (isZerado) {
          const mensagem = hasNoArea
            ? 'Área não informada'
            : hasNoValor
            ? 'Valor do anúncio não informado'
            : 'Valor/m² zerado';

          return (
            <Tooltip title={mensagem}>
              <div style={{ ...styles.valorM2CellStyle, ...zeroCellStyle }}>
                {formatarMoeda(0)}
                <WarningOutlined style={warningIconStyle} />
              </div>
            </Tooltip>
          );
        }

        return (
          <div style={styles.valorM2CellStyle}>
            {formatarMoeda(value)}
          </div>
        );
      },
    },
  ];
}

interface FatorConfig {
  title: string;
  dataIndex: keyof DadosComparativoDireto;
  key: string;
}

const fatorConfigs: Record<string, FatorConfig> = {
  regiao: { title: 'F. Região', dataIndex: 'fatorRegiao', key: 'fatorRegiao' },
  andar: { title: 'F. Andar', dataIndex: 'fatorAndar', key: 'fatorAndar' },
  vaga: { title: 'F. Vaga', dataIndex: 'fatorVaga', key: 'fatorVaga' },
  area: { title: 'F. Área', dataIndex: 'fatorArea', key: 'fatorArea' },
  padraoAcabamento: { title: 'F. Padrão', dataIndex: 'fatorPadraoAcabamento', key: 'fatorPadraoAcabamento' },
  estadoConservacao: { title: 'F. Estado', dataIndex: 'fatorEstadoConservacao', key: 'fatorEstadoConservacao' },
  fatorExtraI: { title: 'F. Extra I', dataIndex: 'fatorExtraI', key: 'fatorExtraI' },
  fatorExtraII: { title: 'F. Extra II', dataIndex: 'fatorExtraII', key: 'fatorExtraII' },
  fatorExtraIII: { title: 'F. Extra III', dataIndex: 'fatorExtraIII', key: 'fatorExtraIII' },
};

function buildFatorColumn(
  config: FatorConfig,
  handleInputChange: (amostraId: string, field: keyof DadosComparativoDireto, value: number) => void,
  disabled?: boolean
): ColumnType {
  return {
    title: config.title,
    dataIndex: config.dataIndex,
    key: config.key,
    width: 120,
    render: (value: number, record: DadosComparativoDireto) => (
      <InputNumber
        value={value}
        // Preserva o 0 digitado (não vira 1) e ignora o campo vazio (null) para
        // o usuário poder apagar tudo e redigitar sem o valor voltar para 1.
        onChange={(newValue) => {
          if (newValue === null) return;
          handleInputChange(record.amostra, config.dataIndex, newValue);
        }}
        style={styles.inputNumberStyle}
        step={0.01}
        precision={2}
        decimalSeparator=","
        disabled={disabled}
      />
    ),
  };
}

export function buildColunasFatores(
  parametrosFatoracao: string[],
  handleInputChange: (amostraId: string, field: keyof DadosComparativoDireto, value: number) => void,
  disabled?: boolean
): ColumnType[] {
  const colunas: ColumnType[] = [];

  colunas.push(buildFatorColumn(
    { title: 'F. Oferta', dataIndex: 'fatorOferta', key: 'fatorOferta' },
    handleInputChange,
    disabled
  ));

  parametrosFatoracao.forEach(parametro => {
    const config = fatorConfigs[parametro];
    if (config) {
      colunas.push(buildFatorColumn(config, handleInputChange, disabled));
    }
  });

  return colunas;
}

export function buildColunasFinais(): ColumnType[] {
  return [
    {
      title: 'FAT',
      dataIndex: 'fat',
      key: 'fat',
      width: 120,
      render: (value: number) => {
        const isZerado = value <= 0.01;

        if (isZerado) {
          return (
            <Tooltip title="FAT zerado - verifique os fatores de correção">
              <div style={{ ...styles.fatCellStyle, ...zeroCellStyle }}>
                {value.toFixed(3)}
                <WarningOutlined style={warningIconStyle} />
              </div>
            </Tooltip>
          );
        }

        return (
          <div style={styles.fatCellStyle}>
            {value.toFixed(3)}
          </div>
        );
      },
    },
    {
      title: 'Valor Homog.',
      dataIndex: 'valorHomogeneizado',
      key: 'valorHomogeneizado',
      width: 150,
      render: (value: number, record: DadosCalculados) => {
        const isZerado = value <= 0;
        const hasNoValorM2 = record.valorM2 <= 0;
        const hasNoFat = record.fat <= 0.01;

        if (isZerado) {
          const mensagem = hasNoValorM2
            ? 'Valor/m² zerado - preencha área e valor do anúncio'
            : hasNoFat
            ? 'FAT zerado - verifique os fatores'
            : 'Valor homogeneizado zerado';

          return (
            <Tooltip title={mensagem}>
              <div style={{ ...styles.valorHomogCellStyle, ...zeroCellStyle }}>
                {formatarMoeda(0)}
                <WarningOutlined style={warningIconStyle} />
              </div>
            </Tooltip>
          );
        }

        return (
          <div style={styles.valorHomogCellStyle}>
            {formatarMoeda(value)}
          </div>
        );
      },
    },
  ];
}

export function createColumns(
  parametrosFatoracao: string[],
  handleInputChange: (amostraId: string, field: keyof DadosComparativoDireto, value: number) => void,
  disabled?: boolean,
  displayIdMap?: Record<string, string>
): ColumnType[] {
  return [
    ...buildColunasBase(displayIdMap),
    ...buildColunasFatores(parametrosFatoracao, handleInputChange, disabled),
    ...buildColunasFinais(),
  ];
}
